import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Calendar, 
  Users, 
  LogOut, 
  ChevronLeft,
  Settings,
  PlusCircle,
  Mail
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    // Get user info
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Tableau de bord' },
    { path: '/admin/evenements', icon: Calendar, label: 'Événements' },
    { path: '/admin/emails', icon: Mail, label: 'Modèles d\'emails' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF7] flex">
      {/* Sidebar */}
      <aside className={`admin-sidebar fixed md:relative h-screen z-40 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://media.ulule.com/presales/3/8/9/8/7/1/178983/logo_v1--2-.5BO4CeI5zf.jpg" 
                alt="Plume et Pion"
                className="h-10 w-auto rounded-xl object-contain flex-shrink-0"
              />
              {sidebarOpen && (
                <div className="overflow-hidden">
                  <h1 className="font-heading font-bold text-white">Plume et Pion</h1>
                  <p className="text-xs text-white/60">Administration</p>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            ))}
            
            <Link
              to="/admin/evenements/nouveau"
              className="admin-nav-link bg-[#ffd451] text-[#2D2A26] hover:bg-[#ffca2c]"
              data-testid="admin-nav-nouveau"
            >
              <PlusCircle className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Nouvel événement</span>}
            </Link>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            {user && sidebarOpen && (
              <div className="flex items-center gap-3 mb-4">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#48b4ff] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {user.name?.charAt(0) || 'A'}
                    </span>
                  </div>
                )}
                <div className="overflow-hidden">
                  <p className="text-white font-medium text-sm truncate">{user.name}</p>
                  <p className="text-white/60 text-xs truncate">{user.email}</p>
                </div>
              </div>
            )}
            
            <button
              onClick={handleLogout}
              className="admin-nav-link w-full text-white/80 hover:text-white"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>Déconnexion</span>}
            </button>
          </div>

          {/* Toggle button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="absolute -right-3 top-20 w-6 h-6 bg-[#2D2A26] rounded-full flex items-center justify-center text-white border-2 border-[#FFFDF7] hover:bg-[#db3b17] transition-colors"
            aria-label={sidebarOpen ? 'Réduire' : 'Agrandir'}
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 min-h-screen transition-all duration-300 ${sidebarOpen ? 'md:ml-0' : 'md:ml-0'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#FFFDF7]/90 backdrop-blur-sm border-b border-[#E5E0D8] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/"
                className="text-[#645E55] hover:text-[#2D2A26] transition-colors"
              >
                <ChevronLeft className="w-5 h-5 inline mr-1" />
                Retour au site
              </Link>
            </div>
            <h1 className="font-heading font-bold text-xl text-[#2D2A26]">{title}</h1>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
